import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { ProductSyncDetail } from '../types';

type OutcomeFilter = 'all' | 'created' | 'updated' | 'skipped' | 'failed';

// Outcome → token classes; every colour comes from the theme tokens.
const OUTCOME_STYLES: Record<string, { dot: string; text: string; badge: string }> = {
  created: { dot: 'bg-success', text: 'text-success', badge: 'border-success/30 bg-success/10 text-success' },
  updated: { dot: 'bg-foreground', text: 'text-foreground', badge: 'border-border bg-muted text-foreground' },
  skipped: { dot: 'bg-warning', text: 'text-warning', badge: 'border-warning/30 bg-warning/10 text-warning' },
  failed: { dot: 'bg-destructive', text: 'text-destructive', badge: 'border-destructive/30 bg-destructive/10 text-destructive' },
};

interface SyncProductDetailsProps {
  productDetails: ProductSyncDetail[];
}

export const SyncProductDetails: React.FC<SyncProductDetailsProps> = ({ productDetails }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [activeFilter, setActiveFilter] = useState<OutcomeFilter>('all');

  const counts = useMemo(() => {
    const c = { created: 0, updated: 0, skipped: 0, failed: 0 };
    for (const d of productDetails) {
      if (d.outcome in c) c[d.outcome]++;
    }
    return c;
  }, [productDetails]);

  const filtered = useMemo(
    () => activeFilter === 'all' ? productDetails : productDetails.filter(d => d.outcome === activeFilter),
    [productDetails, activeFilter],
  );

  const filters: { key: OutcomeFilter; labelKey: string; count: number }[] = [
    { key: 'all', labelKey: 'PAGES.API_INTEGRATION.FILTER_ALL', count: productDetails.length },
    { key: 'created', labelKey: 'PAGES.API_INTEGRATION.SYNC_OUTCOMES.CREATED', count: counts.created },
    { key: 'updated', labelKey: 'PAGES.API_INTEGRATION.SYNC_OUTCOMES.UPDATED', count: counts.updated },
    { key: 'skipped', labelKey: 'PAGES.API_INTEGRATION.SYNC_OUTCOMES.SKIPPED', count: counts.skipped },
    { key: 'failed', labelKey: 'PAGES.API_INTEGRATION.SYNC_OUTCOMES.FAILED', count: counts.failed },
  ];

  const getProductLabel = (detail: ProductSyncDetail) => detail.name || detail.sku || detail.matchValue || '—';

  return (
    <div data-testid="sync-product-details" className="mt-3 overflow-hidden rounded-lg border">
      {/* Header */}
      <div
        data-testid="sync-product-details-header"
        onClick={() => setExpanded(prev => !prev)}
        className="flex cursor-pointer items-center justify-between bg-muted px-3 py-2 transition-colors hover:bg-accent"
      >
        <div className="flex items-center gap-1.5">
          {expanded
            ? <ChevronUp className="size-4 text-muted-foreground" />
            : <ChevronDown className="size-4 text-muted-foreground" />
          }
          <span className="text-xs font-medium text-foreground">
            {t('PAGES.API_INTEGRATION.PRODUCT_DETAILS')}
          </span>
        </div>
        <span className="text-xs text-muted-foreground">
          {t('PAGES.API_INTEGRATION.PRODUCTS_COUNT', { count: productDetails.length })}
        </span>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="px-3 pb-3">
          {/* Filter chips */}
          <div data-testid="sync-product-filters" className="flex flex-wrap gap-1 py-2">
            {filters.map(f => {
              const isActive = activeFilter === f.key;
              const style = f.key === 'all' ? null : OUTCOME_STYLES[f.key];
              return (
                <button
                  key={f.key}
                  type="button"
                  data-testid={`filter-chip-${f.key}`}
                  onClick={() => setActiveFilter(f.key)}
                  className={cn(
                    'flex cursor-pointer items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-medium transition-colors',
                    isActive
                      ? style
                        ? style.badge
                        : 'border-foreground bg-muted text-foreground'
                      : 'border-border text-muted-foreground hover:border-foreground/40',
                  )}
                >
                  {t(f.labelKey)}
                  <span className="text-[9px] opacity-80">{f.count}</span>
                </button>
              );
            })}
          </div>

          {/* Product list */}
          <div data-testid="sync-product-list" className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <p
                data-testid="sync-product-empty"
                className="py-4 text-center text-xs text-muted-foreground"
              >
                {t('PAGES.API_INTEGRATION.NO_PRODUCTS_IN_CATEGORY')}
              </p>
            ) : (
              filtered.map((detail, idx) => {
                const style = OUTCOME_STYLES[detail.outcome] || OUTCOME_STYLES.failed;
                return (
                  <div
                    key={idx}
                    data-testid={`sync-product-row-${idx}`}
                    className={cn(
                      'flex items-start justify-between py-1.5',
                      idx < filtered.length - 1 && 'border-b',
                    )}
                  >
                    <div className="flex min-w-0 flex-1 items-start gap-2">
                      {/* Status dot */}
                      <span className={cn('mt-1.5 size-1.5 shrink-0 rounded-full', style.dot)} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium text-foreground">
                          {getProductLabel(detail)}
                        </p>
                        {detail.sku && detail.name && (
                          <p className="text-[10px] text-muted-foreground">
                            sku: {detail.sku}
                          </p>
                        )}
                        {detail.outcome === 'failed' && detail.error && (
                          <p className="mt-0.5 font-mono text-[10px] leading-relaxed text-destructive">
                            {detail.error}
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Outcome badge */}
                    <span
                      className={cn(
                        'ms-2 shrink-0 rounded-md border px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide',
                        style.badge,
                      )}
                    >
                      {t(`PAGES.API_INTEGRATION.SYNC_OUTCOMES.${detail.outcome.toUpperCase()}`)}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
